import { TreeManager, TreeNode } from '@basehub/utils/tree/index'
import {
  Block,
  DateBlock,
  ReferenceBlock,
  IconBlock,
  MediaBlock,
} from '../schema/block'
import { AtLeast } from '../helpers'
import {
  isBlockWithVariantOverrides,
  isTypeForBlockWithChildren,
} from '../schema/type-casts'
import { invariant } from '@epic-web/invariant'
import { getApiNameWithoutCollisions } from '../../api-kv'
import { getApiNameAndSlug, getPreviewLinkV2 } from './helpers'
import type { LiteBlock } from '../schema/block/instance'
import { makeInstanceChildBlock } from '../schema/block/instance'
import {
  VariantData,
  getValueBasedOnSelectedVariants,
} from '../helpers/variants'

export type Option = {
  name: string
  displayLabel?: string
  value?: string
  node: TreeNode<LiteBlock> | null
  hidden?: boolean
}

/**
 * This function receives an array of segments and the nodeBlock (as the origin block where the user is currently at)
 * and processes the segments one by one and returns the possible variables at the edge of the current segment.
 */

export const getPossibleVariables = ({
  segments,
  nodeBlock,
  treeManager,
  valueKey = 'value',
  rootOptions,
  selectedVariants,
}: {
  segments: string[]
  nodeBlock: TreeNode<LiteBlock>
  treeManager: AtLeast<TreeManager<LiteBlock>, 'getChildrenKeys' | 'getNode'>
  valueKey?: keyof Block
  rootOptions?: Option[]
  selectedVariants: VariantData[] | null
}): Option[] => {
  let currentSegmentOptions: Option[] | null = [{ node: nodeBlock, name: '' }]

  if (segments[0] !== '') {
    segments.unshift('')
  }

  const getValueWithVariants = (block: LiteBlock) => {
    if (valueKey !== 'value' || !isBlockWithVariantOverrides(block)) {
      return block[valueKey]
    }
    if (selectedVariants?.length) {
      return getValueBasedOnSelectedVariants({
        block,
        variantsToFilterBy: selectedVariants,
      })
    }
    return block[valueKey]
  }

  for (let i = 0; i < segments.length; i++) {
    const segment = segments[i]
    const option = currentSegmentOptions.find((o) => o.name === segment)
    if (!option || !option.node) {
      // break the chain
      return []
    }

    let nextOptions: Option[] = []

    switch (segment) {
      case 'blocks': {
        const children = treeManager
          .getChildrenKeys(option.node.key)
          ?.map((childKey) => treeManager.getNode(childKey, { required: true }))
          ?.sort((a, b) => {
            return (
              ('apiName' in a.value && a.value.apiName ? 0 : 1) -
              ('apiName' in b.value && b.value.apiName ? 0 : 1)
            )
          })
        invariant(children, 'children not found')

        const buffer = new Set<string>()

        for (const _child of children) {
          const child = { ..._child }
          if (option.node.value.type === 'instance') {
            invariant(
              child.value.instanceChildTarget,
              'instanceChildTarget not found'
            )
            child.value = makeInstanceChildBlock({
              block: child.value,
              parentSourceBlockId:
                option.node.value.sourceBlockId || option.node.value.id,
              targetBlock: treeManager.getNode(
                child.value.instanceChildTarget,
                { required: true }
              ).value,
            })
          }

          const apiName =
            child.value.apiName ??
            getApiNameWithoutCollisions(
              child.value.id,
              child.value.title,
              buffer
            )
          buffer.add(apiName)
          switch (child.value.type) {
            case 'number':
            case 'text':
              nextOptions.push({
                name: apiName,
                value: String(getValueWithVariants(child.value) || ''),
                node: child,
              })
              break
            case 'date': {
              let formattedDate = ''
              const value = getValueWithVariants(child.value)
              if (value && valueKey === 'value' && typeof value === 'string') {
                try {
                  const date = new Date(value)
                  formattedDate = `${date.toLocaleString('default', {
                    month: 'long',
                  })} ${date.getDate()}, ${date.getFullYear()}`
                } catch (error) {
                  // invalid date, do nothing
                }
              }

              nextOptions.push({
                name: apiName,
                value: String(formattedDate || value || ''),
                node: child,
              })
              break
            }
            case 'reference': {
              const value = getValueWithVariants(
                child.value
              ) as ReferenceBlock['value']
              const targetBlockId =
                value?.targetBlockIds?.[0] ??
                child.value.constraints?.acceptedTypes?.[0] ??
                ''
              if (!targetBlockId) {
                break
              }
              // Prevent circular references
              const targetNodeBlock = treeManager.getNode(targetBlockId)
              if (
                !targetNodeBlock ||
                targetNodeBlock.value.id === child.value.id
              ) {
                break
              }

              nextOptions.push({ name: apiName, node: targetNodeBlock })
              break
            }
            case 'media': {
              const value =
                valueKey === 'value'
                  ? (getValueWithVariants(child.value) as MediaBlock['value'])
                  : String(child.value[valueKey] || '')
              nextOptions.push({
                name: apiName,
                node: child,
                value:
                  typeof value !== 'string'
                    ? value?.status === 'uploaded' && value?.url
                      ? value.url
                      : ''
                    : value,
              })
              break
            }
            case 'instance':
            case 'component':
            case 'document':
              nextOptions.push({ name: apiName, node: child })
              break
            case 'icon': {
              const value =
                (valueKey === 'value'
                  ? (getValueWithVariants(child.value) as IconBlock['value'])
                  : String(child.value[valueKey] || '')) || '<svg></svg>'
              nextOptions.push({
                name: apiName,
                node: child,
                value,
              })
              break
            }
            default:
              break
          }
        }

        break
      }
      default: {
        const parent = option.node.parentKey
          ? treeManager.getNode(option.node.parentKey, { required: true })
          : null

        option.node = { ...option.node }

        if (parent && parent.value.type === 'instance') {
          invariant(
            option.node.value.instanceChildTarget,
            'instanceChildTarget not found'
          )
          option.node.value = makeInstanceChildBlock({
            block: option.node.value,
            parentSourceBlockId: parent.value.sourceBlockId || parent.value.id,
            targetBlock: treeManager.getNode(
              option.node.value.instanceChildTarget,
              { required: true }
            ).value,
          })
        }

        const blockOwn: Option[] = []
        if (isTypeForBlockWithChildren(option.node.value.type)) {
          const { _apiName, _slug } = getApiNameAndSlug({
            nodeBlock: option.node,
            treeManager,
          })
          blockOwn.push(
            {
              name: '_title',
              value: option.node.value.title,
              node: null,
            },
            {
              name: '_slug',
              value: _slug,
              node: null,
            },
            {
              name: '_apiName',
              value: _apiName,
              node: null,
            }
          )

          /**
           * Children
           */

          const children = treeManager
            .getChildrenKeys(option.node.key)
            ?.map((key) => {
              return treeManager.getNode(key, { required: true })
            })
            ?.sort((a, b) => {
              return (
                ('apiName' in a.value && a.value.apiName ? 0 : 1) -
                ('apiName' in b.value && b.value.apiName ? 0 : 1)
              )
            })
          invariant(children, 'children not found')

          const buffer = new Set<string>()

          for (const _child of children) {
            const child = { ..._child }
            if (option.node.value.type === 'instance') {
              invariant(
                child.value.instanceChildTarget,
                'instanceChildTarget not found'
              )
              child.value = makeInstanceChildBlock({
                block: child.value,
                parentSourceBlockId:
                  option.node.value.sourceBlockId || option.node.value.id,
                targetBlock: treeManager.getNode(
                  child.value.instanceChildTarget,
                  { required: true }
                ).value,
              })
            }

            const apiName =
              child.value.apiName ??
              getApiNameWithoutCollisions(
                child.value.id,
                child.value.title,
                buffer
              )
            buffer.add(apiName)

            switch (child.value.type) {
              case 'number':
              case 'text':
                blockOwn.push({
                  name: apiName,
                  value: String(getValueWithVariants(child.value) || ''),
                  node: child,
                })
                break
              case 'workflow':
                blockOwn.push({ name: apiName, node: child })
                break
              case 'date': {
                let formattedDate = ''
                if (
                  valueKey === 'value' &&
                  typeof getValueWithVariants(child.value) === 'string'
                ) {
                  const value = getValueWithVariants(
                    child.value
                  ) as DateBlock['value']
                  if (value) {
                    try {
                      const date = new Date(value)
                      formattedDate = `${date.toLocaleString('default', {
                        month: 'long',
                      })} ${date.getDate()}, ${date.getFullYear()}`
                    } catch (error) {
                      // invalid date, do nothing
                    }
                  }
                }

                blockOwn.push({
                  name: apiName,
                  value: String(
                    formattedDate || getValueWithVariants(child.value) || ''
                  ),
                  node: child,
                })
                break
              }
              case 'reference': {
                const value = getValueWithVariants(
                  child.value
                ) as ReferenceBlock['value']
                const targetBlockId =
                  value?.targetBlockIds?.[0] ??
                  child.value.constraints?.acceptedTypes?.[0] ??
                  ''
                if (!targetBlockId) {
                  break
                }
                // Prevent circular references
                const targetNodeBlock = treeManager.getNode(targetBlockId)
                if (
                  !targetNodeBlock ||
                  targetNodeBlock.value.id === child.value.id
                ) {
                  break
                }

                blockOwn.push({ name: apiName, node: targetNodeBlock })
                break
              }
              case 'media': {
                const value =
                  valueKey === 'value'
                    ? (getValueWithVariants(child.value) as MediaBlock['value'])
                    : String(child.value[valueKey] || '')
                blockOwn.push({
                  name: apiName,
                  node: child,
                  value:
                    typeof value !== 'string'
                      ? value?.status === 'uploaded' && value?.url
                        ? value.url
                        : ''
                      : value,
                })
                break
              }
              case 'icon':
                blockOwn.push({
                  name: apiName,
                  node: child,
                  value:
                    valueKey === 'value'
                      ? ((getValueWithVariants(
                          child.value
                        ) as IconBlock['value']) ?? '<svg></svg>')
                      : String(getValueWithVariants(child.value) || '') ||
                        '<svg></svg>',
                })
                break
              case 'instance':
              case 'component':
              case 'document':
                blockOwn.push({ name: apiName, node: child })
                break
              default:
                break
            }
          }

          if (parent) {
            blockOwn.push({
              name: '_parent',
              value: parent.value.title,
              node: parent,
            })
            if (blockOwn.some((o) => o.name === 'parent') === false) {
              // backwards compat
              blockOwn.push({
                name: 'parent',
                value: parent.value.title,
                node: parent,
                hidden: true,
              })
            }
          }
          blockOwn.push({
            name: '_previewUrl',
            value: `Reuse this block's preview URL`,
            node: option.node,
          })
          if (blockOwn.some((o) => o.name === 'blocks') === false) {
            // backwards compat
            blockOwn.push({ name: 'blocks', node: option.node, hidden: true })
          }
        }

        switch (option.node.value.type) {
          case 'media': {
            const value =
              valueKey === 'value'
                ? (getValueWithVariants(
                    option.node.value
                  ) as MediaBlock['value'])
                : String(option.node.value[valueKey] || '')
            blockOwn.push({
              name: 'url',
              value:
                typeof value !== 'string'
                  ? value?.status === 'uploaded' && value?.url
                    ? value.url
                    : ''
                  : value,
              node: option.node,
            })
            break
          }
          case 'workflow': {
            // add commit in case of "commit" trigger
            // add instance block in case of "row" trigger
            // add event data in case of "event" trigger

            // blockOwn.push({
            //   name: 'trigger',
            //   value:
            //     valueKey === 'value'
            //       ? (getValueWithVariants(option.node.value) as ImageBlock['value'])?.status === 'uploaded' &&
            //         (getValueWithVariants(option.node.value) as ImageBlock['value'])?.url
            //         ? (getValueWithVariants(option.node.value) as ImageBlock['value']).url
            //         : ''
            //       : String(getValueWithVariants(option.node.value) || ''),
            //   node: option.node,
            // })
            break
          }
          default:
            break
        }

        nextOptions = [...blockOwn]
        break
      }
    }

    currentSegmentOptions = nextOptions
    if (i === 0) {
      currentSegmentOptions.push(...(rootOptions ?? []))
    }
  }

  return currentSegmentOptions
}

const varPattern = /\{([^{}]+)\}/g

export type GetVariables = (segments: string[]) => Option[]
export type GetPreviewUrl = (node: TreeNode<LiteBlock>) => string

export function resolveInputWithVars(
  inputString: string,
  opts: {
    getVariables: GetVariables
    getPreviewUrl: GetPreviewUrl
    debug?: boolean
  }
): string {
  try {
    const replacedString = inputString.replace(varPattern, (_match, key) => {
      const trimmedKey = key.trim()
      const segments = trimmedKey.split('.')
      const lastSegment = segments.pop()
      const options = opts.getVariables(segments)
      const option = options.find((o) => o.name === lastSegment)

      if (opts.debug) {
        /* eslint-disable no-console */
        console.log('inputString', inputString)
        console.log('options', options)
        console.log('option', option)
        /* eslint-enable no-console */
      }

      if (option?.name === '_previewUrl') {
        if (!option.node) return ''
        return opts.getPreviewUrl(option.node)
      }

      const value = option?.value
      return value !== undefined ? value : ''
    })

    return replacedString
  } catch (error) {
    console.error(error)
    return inputString
  }
}

export function getPreviewURL({
  nodeBlock,
  blockTreeManager,
  previewToken,
  previewRef,
  rootOptions,
  origin,
  selectedVariants,
}: {
  nodeBlock: TreeNode<LiteBlock>
  blockTreeManager: TreeManager<LiteBlock>
  previewToken?: string
  previewRef?: string
  rootOptions: Option[]
  origin: string
  selectedVariants: VariantData[] | null
}): string {
  if (!blockTreeManager) return ''

  const [previewLinkPure, nodeToGetVariablesFrom] = getPreviewLinkV2(
    nodeBlock,
    blockTreeManager
  )

  const previewLinkWithVars = resolveInputWithVars(previewLinkPure, {
    getVariables: (segments) => {
      return getPossibleVariables({
        segments,
        nodeBlock: nodeToGetVariablesFrom,
        treeManager: blockTreeManager,
        valueKey: 'value',
        rootOptions,
        selectedVariants,
      })
    },
    getPreviewUrl: (node) => {
      if (node.key === nodeBlock.key) {
        // prevent infinite loop
        return ''
      }
      return getPreviewURL({
        nodeBlock: node,
        blockTreeManager,
        previewToken: undefined,
        rootOptions,
        origin,
        selectedVariants,
      })
    },
  })

  try {
    const regex = /^(https?:\/\/)?([\da-z.-]+)\.([a-z.]{2,6})([/\w .-]*)*\/?$/

    const url = regex.test(previewLinkWithVars)
      ? // if the previewLinkWithVars looks like a domain, we should not attach the origin from params
        new URL(previewLinkWithVars)
      : new URL(previewLinkWithVars, origin)

    if (previewRef) {
      url.searchParams.append('bshb-preview-ref', previewRef)
    }
    if (previewToken) {
      url.searchParams.append('bshb-preview', previewToken)
    }

    // prevent double/triple slashes
    url.href = url.href.replace(/([^:]\/)\/+/g, '$1')

    if (url.href.startsWith(origin)) {
      if (url.href.startsWith(origin + '/') === false) {
        url.href = url.href.replace(origin, origin + '/')
      }
    }

    // cleanup slash + query
    url.href = url.href.replace('/?', '?')

    return url.toString()
  } catch (err) {
    // probably an invalid url, ignore and don't append anything to it
    return previewLinkWithVars
  }
}
